import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Breadcrumbs from "../../../../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import { Button, FormGroup, Label, Spinner } from "reactstrap";
import userService from "services/UserService";
import SetUserPerfilModal from "./SetUserPerfil";
import authPermissionService from "services/AuthPermissionService";
import authUserRoleService from "services/AuthUserRoleService";
import { Modal, ModalBody } from "reactstrap";
import Select from "react-select";
import { customStyles } from "pages/app-views/indicadores/Form";
import permissionsService from "services/permissionsService";
import Loading from "components/Common/Loading";
import DynamicMaterialTable from "components/Common/MaterialTableContainer";
import useTableZoom from "components/Common/useTableZoom";
import PermissoesModal from "./PermissoesModal";
import TransferAndDeactivateModal from "./TransferAndDeactivateModal";
import CopyPerfilModal from "./CopyPerfilModal";
import Swal from "sweetalert2";
import { IconButton, Tooltip } from "@mui/material";
import { PencilSimple, Trash, UserCheck } from "@phosphor-icons/react";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";
import {
	getTableBodyCellProps,
	getTableHeaderCellProps,
} from "components/Common/MaterialTableStyles";

const Usuarios = () => {
	// ========== CONFIGURAÇÃO DE LARGURAS MÍNIMAS E MÁXIMAS DAS COLUNAS ==========
	const COLUMN_WIDTHS = {
		name: { min: 130 },                    // Largura mínima da coluna "Nome Completo" (sem máximo - cresce livremente)
		lastname: { min: 100 },      // Largura mín/máx da coluna "Apelido" (em pixels)
		email: { min: 220 },         // Largura mín/máx da coluna "Email" (em pixels)
		phone: { min: 160 },         // Largura mín/máx da coluna "Contato da Empresa" (em pixels)
		userPermission: { min: 100, max: 150 },    // Largura mín/máx da coluna "Permissão" (em pixels)
		permission: { min: 100, max: 120 },    // Largura mín/máx da coluna "Permissões" (em pixels)
		actions: { min: 70, max: 70 },       // Largura mín/máx da coluna "Ações" (em pixels)
	};
	// ================================================================

	const [data, setData] = useState([]);
	const [openModal, setOpenModal] = useState(false);
	const [userDataModal, setUserDataModal] = useState("");
	const [allPermissions, setAllPermissions] = useState();
	const [allUserPermissions, setAllUserPermissions] = useState();
	const [isOpen, setIsOpen] = useState(false);
	const [userIdDesativate, setUserIdDesativate] = useState();
	const [userIdTransfer, setUserIdTransfer] = useState();
	const [usuariosList, setUsuariosList] = useState([]);
	const [permissions, setPermissions] = useState([]);
	const [temPermissaoAdicionar, setTemPermissaoAdicionar] = useState(false);
	const [temPermissaoEditar, setTemPermissaoEditar] = useState(false);
	const [temPermissaoVisualizar, setTemPermissaoVisualizar] = useState(false);
	const [temPermissaoExcluir, setTemPermissaoExcluir] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
	const [copyPerfilModalOpen, setCopyPerfilModalOpen] = useState(false);
	const [selectedUserPermissions, setSelectedUserPermissions] = useState([]);
	const [tooltipOpen, setTooltipOpen] = useState(false);

	// Usar o hook customizado para zoom e cálculos de largura
	const { zoomLevel, getColumnWidth } = useTableZoom(COLUMN_WIDTHS);

	const toggleTooltip = () => {
		setTooltipOpen(!tooltipOpen);
	};

	const navigate = useNavigate();

	document.title = "Senda | Usuarios";

	const columns = useMemo(
		() => [
			{
				header: "Nome Completo",
				accessorKey: "name",
				filterFn: "contains",
				widthPixels: getColumnWidth('name'),
				minWidthPixels: COLUMN_WIDTHS.name.min,
				// Não tem maxWidthPixels - pode crescer livremente
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("firstColumn"),
			},
			{
				header: "Apelido",
				accessorKey: "lastname",
				filterFn: "contains",
				widthPixels: getColumnWidth('lastname'),
				minWidthPixels: COLUMN_WIDTHS.lastname.min,
				maxWidthPixels: COLUMN_WIDTHS.lastname.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
			},
			{
				header: "Email",
				accessorKey: "email",
				filterFn: "contains",
				widthPixels: getColumnWidth('email'),
				minWidthPixels: COLUMN_WIDTHS.email.min,
				maxWidthPixels: COLUMN_WIDTHS.email.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
			},
			{
				header: "Contato da Empresa",
				accessorKey: "phone",
				filterFn: "contains",
				widthPixels: getColumnWidth('phone'),
				minWidthPixels: COLUMN_WIDTHS.phone.min,
				maxWidthPixels: COLUMN_WIDTHS.phone.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
			},
			{
				header: "Permissão",
				accessorKey: "userPermission",
				enableColumnFilter: false,
				enableSorting: false,
				widthPixels: getColumnWidth('userPermission'),
				minWidthPixels: COLUMN_WIDTHS.userPermission.min,
				maxWidthPixels: COLUMN_WIDTHS.userPermission.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				Cell: ({ row }) => {
					// Obtém o company_id atual do localStorage
					const currentCompanyId = localStorage.getItem("company_id");
					// Busca a permissão do usuário no array allUserPermissions
					// Filtrando pela empresa atual
					const userPermission = allUserPermissions?.find(
						(aup) =>
							aup.user &&
							aup.user.id == row.original.id &&
							aup.company_id == currentCompanyId
					);

					return (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								width: "100%",
								height: "100%",
								fontSize: "12px",
								fontWeight: "500",
								textAlign: "center",
								wordWrap: "break-word",
								whiteSpace: "normal",
							}}
						>
							<span style={{ textAlign: "center" }}>
								{userPermission?.permission?.name || ""}
							</span>
						</div>
					);
				},
			},
			{
				header: "Permissões",
				accessorKey: "permission",
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				widthPixels: getColumnWidth('permission'),
				minWidthPixels: COLUMN_WIDTHS.permission.min,
				maxWidthPixels: COLUMN_WIDTHS.permission.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				Cell: ({ row }) => (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: "100%",
							height: "100%",
						}}
					>
						<Button
							style={{
								background: "none",
								padding: "8px",
								cursor: "pointer",
								color: "black",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 2,
								fontSize: "12px",
								border: "1px solid #ccc",
								borderRadius: "4px",
							}}
							onClick={() => {
								const userPermissions =
									allUserPermissions.filter(
										(aup) =>
											aup.user && aup.user.id === row.original.id,
									);
								setSelectedUserPermissions(userPermissions);
								setPermissionsModalOpen(true);
							}}
						>
							Ver Permissões
						</Button>
					</div>
				),
			},
			{
				header: <span>Ações</span>,
				accessorKey: "actions",
				widthPixels: getColumnWidth('actions'),
				minWidthPixels: COLUMN_WIDTHS.actions.min,
				maxWidthPixels: COLUMN_WIDTHS.actions.max,
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("actionColumn"),
				Cell: ({ row }) => (
					<div style={{
						display: "flex",
						flexWrap: "wrap",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						height: "100%",
						gap: "2px",
					}}>
						{temPermissaoEditar && (
							<>
								<Tooltip title="Editar" placement="top">
									<IconButton
										id={`editar-${row.original?.id || 'unknown'}`}
										style={{
											background: "none",
											border: "none",
											padding: "1px",
											cursor: "pointer",
											color: "black",
											minWidth: "auto",
											width: "auto",
											height: "auto",
										}}
										onClick={() => editarItem(row.original)}
									>
										<PencilSimple size={14} />
									</IconButton>
								</Tooltip>
								<Tooltip title="Permissões" placement="top">
									<IconButton
										style={{
											background: "none",
											border: "none",
											padding: "1px",
											cursor: "pointer",
											color: "black",
											minWidth: "auto",
											width: "auto",
											height: "auto",
										}}
										onClick={() => editPermissionModal(row.original)}
									>
										<UserCheck size={14} />
									</IconButton>
								</Tooltip>
							</>
						)}
						{temPermissaoExcluir && (
							<Tooltip title="Excluir" placement="top">
								<IconButton
									style={{
										background: "none",
										border: "none",
										padding: "1px",
										cursor: "pointer",
										color: "black",
										minWidth: "auto",
										width: "auto",
										height: "auto",
									}}
									onClick={() => handleInactivateUser(row.original)}
								>
									<Trash size={14} />
								</IconButton>
							</Tooltip>
						)}
					</div>
				),
				align: "center",
			},
		],
		[
			allUserPermissions,
			temPermissaoEditar,
			temPermissaoExcluir,
			openModal,
			zoomLevel, // Adicionado zoomLevel como dependência
			getColumnWidth, // Adicionado getColumnWidth como dependência
		],
	);

	const editarItem = (rowData) => {
		navigate(`/form-usuario/${rowData.id}`);
	};

	const editPermissionModal = (rowData) => {
		setUserDataModal(rowData);
		setOpenModal(true);
	};

	const handleInactivateUser = async (rowData) => {
		setUserIdDesativate(rowData.id);
		handleOpen();
	};

	function onCloseModal() {
		setOpenModal(false);
	}

	const saveChangePerfil = async (data) => {
		try {
			if (data) {
				const response = await authUserRoleService.post(data);
				getUserPermissions();
				return response;
			}
		} catch (error) {
			console.log(error);
			throw error;
		}
	};

	const getItems = async () => {
		try {
			const response = await userService.get();
			setData(response);
		} catch (error) {
			console.log(error);
		}
	};

	const getPermissionsUser = async () => {
		try {
			const response = await permissionsService.getPermissions();
			for (let permission of response) {
				if (permission.role === "CollaboratorUpdate") {
					setTemPermissaoEditar(true);
				}
				if (permission.role === "CollaboratorCreate") {
					setTemPermissaoAdicionar(true);
				}
				if (permission.role === "CollaboratorList") {
					setTemPermissaoVisualizar(true);
				}
				if (permission.role === "CollaboratorDelete") {
					setTemPermissaoExcluir(true);
				}
			}
		} catch (error) {
			console.log(error);
		}
	};

	const getPermissions = async () => {
		try {
			const response = await authPermissionService.get();
			setAllPermissions(response);
		} catch (error) {
			console.log(error);
		}
	};

	const getUserPermissions = async () => {
		try {
			const response = await authUserRoleService.get();
			setAllUserPermissions(response);
		} catch (error) {
			console.log(error);
		}
	};

	const handleSaveCopiedPerfis = async ({
		targetUserId,
		targetPermissions,
		profilesToAdd,
	}) => {
		const payloadExistente = (targetPermissions || []).map((role) => ({
			permission_id: String(role.permission_id || role?.permission?.id),
			user_id: targetUserId,
			company_id: role.company_id || role?.company?.id,
			type: role.type || "0",
		}));

		const payloadAdicionar = (profilesToAdd || []).map((role) => ({
			permission_id: String(role.permission_id || role?.permission?.id),
			user_id: targetUserId,
			company_id: role.company_id || role?.company?.id,
			type: role.type || "0",
		}));

		const payloadFinal = [...payloadExistente, ...payloadAdicionar];

		if (!payloadFinal.length) return;

		await saveChangePerfil(payloadFinal);
		await getUserPermissions();

		SendaCustomSwal.fire({
			title: "Sucesso",
			text: "Perfis copiados com sucesso.",
			iconHtml: '<i class="success-icon"></i>',
			customClass: {
				confirmButton: "custom-confirm-button",
			},
		});
	};

	const getAll = async () => {
		setIsLoading(true);
		await getPermissionsUser();
		await getPermissions();
		await getItems();
		await getUserPermissions();
		setIsLoading(false);
	};

	useEffect(() => {
		getAll();
	}, []);

	useEffect(() => {
		getUserPermissions();
	}, [openModal]);

	const handleClose = () => {
		setUserIdDesativate();
		setUserIdTransfer();
		setIsOpen(false);
	};
	const handleOpen = () => {
		getUsuarios();
		setIsOpen(true);
	};

	const handleDesactivate = async (transferData) => {
		try {
			const {
				userIdDesativate,
				indicatorsUserId,
				actionPlansUserId,
				actionPlansApproverId,
				actionPlansObjectivesId,
				ataUserId,
			} = transferData;

			const payload = {
				indicatorsUserId,
				actionPlansUserId,
				actionPlansApproverId,
				actionPlansObjectivesId,
				ataUserId,
			};

			await userService.desactivate(userIdDesativate, payload);

			// Exibe o alerta de sucesso
			SendaCustomSwal.fire({
				title: "Sucesso!",
				text: "Usuário transferido e desativado com sucesso.",
				iconHtml: '<i class="success-icon"></i>',
				confirmButtonText: "OK",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			}).then(async () => {
				// Após clicar em "OK", atualiza a lista e fecha o modal
				await getItems();
				handleClose();
			});
		} catch (error) {
			console.log(error);
		}
	};

	async function getUsuarios() {
		try {
			const response = await userService.get();
			const users = response.map((item) => {
				return {
					value: item.id,
					label: item.lastname,
				};
			});
			setUsuariosList(users);
		} catch (error) {
			console.log(error);
		}
	}

	if (!temPermissaoVisualizar) return;

	return (
		<Loading isLoading={isLoading}>
			<div className="page-content">
				<PermissoesModal
					isOpen={permissionsModalOpen}
					toggle={() =>
						setPermissionsModalOpen(!permissionsModalOpen)
					}
					permissions={selectedUserPermissions}
				/>
				<TransferAndDeactivateModal
					isOpen={isOpen}
					toggle={handleClose}
					userIdDesativate={userIdDesativate}
					handleDesactivate={handleDesactivate}
					usuariosList={usuariosList}
				/>
				{temPermissaoAdicionar && (
					<Breadcrumbs
						botaoLink={"/form-usuario"}
						botao={"Adicionar Usuário"}
						title="Usuários"
						extraButton={{
							label: "Copiar Perfil",
							icon: "bx bx-copy",
							onClick: () => setCopyPerfilModalOpen(true),
						}}
					/>
				)}
				<div
					className="container-fluid"
					style={{
						backgroundColor: "white",
						padding: 20,
						borderRadius: 10,
						marginTop: 20,
					}}
				>
					<DynamicMaterialTable columns={columns} data={data} />
				</div>
				<SetUserPerfilModal
					show={openModal}
					onCloseClick={onCloseModal}
					onChangeClick={saveChangePerfil}
					nomePerfil={userDataModal}
					permissions={allPermissions}
				/>
				<CopyPerfilModal
					isOpen={copyPerfilModalOpen}
					toggle={() => setCopyPerfilModalOpen(!copyPerfilModalOpen)}
					users={data}
					allUserPermissions={allUserPermissions}
					onSaveCopy={handleSaveCopiedPerfis}
				/>
			</div>
		</Loading>
	);
};

Usuarios.propTypes = {
	preGlobalFilteredRows: PropTypes.any,
};

export default withTranslation()(Usuarios);
